import { useState, useMemo } from 'react'
import { useDashReport } from '../hooks/useDashReport'

interface EspecialidadItem {
  especialidad: string
  total: number
  porcentaje: number
  color?: string
}

interface OpellaPanamaData {
  titulo: string
  subtitulo: string
  total_medicos: number
  total_especialidades: number
  especialidades: EspecialidadItem[]
  google_sheets_url?: string
}

export function OpellaPanamaPage() {
  const { data, isLoading, error } = useDashReport<OpellaPanamaData>('opella/panama')
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('todas')
  const [especialidadesExpanded, setEspecialidadesExpanded] = useState(false)

  const filteredData = useMemo(() => {
    if (!data) return null
    if (filtroEspecialidad === 'todas') {
      return {
        tituloResumen: 'Resumen General',
        totalMedicos: data.total_medicos,
        totalEspecialidades: data.total_especialidades,
        especialidades: data.especialidades,
      }
    }
    const espData = data.especialidades.find((e) => e.especialidad === filtroEspecialidad)
    if (espData) {
      return {
        tituloResumen: filtroEspecialidad,
        totalMedicos: espData.total,
        totalEspecialidades: 1,
        especialidades: [espData],
      }
    }
    return {
      tituloResumen: 'Resumen General',
      totalMedicos: data.total_medicos,
      totalEspecialidades: data.total_especialidades,
      especialidades: data.especialidades,
    }
  }, [data, filtroEspecialidad])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !filteredData) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const showGoogleSheets = data.google_sheets_url && data.google_sheets_url !== '#'

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
        .animate-pulse-subtle.expanded { animation: none; opacity: 1; transform: scale(1); }
      `}</style>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://enterprise.tonalstrategies.com/pricing/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              {showGoogleSheets && (
                <a
                  href={data.google_sheets_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 2h8v2H6V6zm0 4h8v2H6v-2zm0 4h4v2H6v-2z" />
                  </svg>
                  Ver Datos Fuente
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Filtro de Especialidad */}
            <div>
              <label htmlFor="select-especialidad" className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                Especialidad
              </label>
              <select
                id="select-especialidad"
                value={filtroEspecialidad}
                onChange={(e) => setFiltroEspecialidad(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="todas">Todas las especialidades</option>
                {data.especialidades.map((esp) => (
                  <option key={esp.especialidad} value={esp.especialidad}>
                    {esp.especialidad} ({esp.total} médicos)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>{filteredData.tituloResumen}</span>
          </h2>

          {/* Totales */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{filteredData.totalMedicos.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-indigo-600">{filteredData.totalEspecialidades}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades Activas</p>
            </div>
          </div>
        </div>

        {/* Desglose por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspecialidadesExpanded(!especialidadesExpanded)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="text-base lg:text-lg">Análisis por Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">
                  ({filteredData.especialidades.length} especialidad{filteredData.especialidades.length !== 1 ? 'es' : ''})
                </span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${
                  especialidadesExpanded ? 'rotate-180 expanded' : 'animate-pulse-subtle'
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {especialidadesExpanded && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredData.especialidades.map((esp) => (
                    <tr key={esp.especialidad} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp.especialidad}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{esp.total}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-indigo-600">{esp.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${esp.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{filteredData.totalMedicos}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-indigo-600">100%</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Análisis de Base de Datos</p>
        </div>
      </div>
    </div>
  )
}
